import React, { useEffect, useRef, useState } from 'react';
import { useSidebar } from './SidebarContext';
import logo from '../assets/images/logo.png';

export const SidebarButton: React.FC = () => {
  const { toggleSidebar, isOpen } = useSidebar();
  const [visualState, setVisualState] = useState<'normal' | 'faded' | 'hidden'>('normal');
  const idleTimerRef = useRef<NodeJS.Timeout | null>(null);
  const fadeTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Idle detection: normal → faded (2.5s) → hidden (3.5s)
  useEffect(() => {
    const resetIdle = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);

      setVisualState('normal');

      idleTimerRef.current = setTimeout(() => {
        setVisualState('faded');

        fadeTimerRef.current = setTimeout(() => {
          setVisualState('hidden');
        }, 1000);
      }, 2500);
    };

    // Reset on user activity
    const events: (keyof DocumentEventMap)[] = [
      'mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'touchmove',
    ];
    events.forEach((evt) =>
      document.addEventListener(evt, resetIdle, { passive: true })
    );

    // Start timer on mount
    resetIdle();

    return () => {
      events.forEach((evt) => document.removeEventListener(evt, resetIdle));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
    };
  }, []);

  // Always show when sidebar is open
  const effectiveState = isOpen ? 'normal' : visualState;

  const opacity =
    effectiveState === 'normal' ? 1 :
    effectiveState === 'faded' ? 0.35 :
    0.08;

  const scale =
    effectiveState === 'normal' ? 1 :
    effectiveState === 'faded' ? 0.92 :
    0.85;

  return (
    <button
      onClick={toggleSidebar}
      aria-label="Toggle sidebar menu"
      style={{
        position: 'fixed',
        right: '16px',
        top: '50%',
        transform: `translateY(-50%) scale(${scale})`,
        zIndex: 1000000,
        width: '30px',
        height: '30px',
        background: 'transparent',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity,
        transition: 'opacity 0.5s ease, transform 0.5s ease',
        pointerEvents: effectiveState === 'hidden' ? 'none' : 'auto',
      }}
    >
      <img
        src={logo}
        alt="Menu"
        style={{
          width: '30px',
          height: '30px',
          objectFit: 'contain',
          borderRadius: '50%',
          display: 'block',
          pointerEvents: 'none',
        }}
      />
    </button>
  );
};